import React from 'react';
import { useParams, Link } from 'react-router-dom';

const ProjectDetail = () => {
  const { name } = useParams();

  const projects = {
    'skepa-lang': {
      name: 'Skepa-Lang',
      coverImage: '/assets/skepalang.png',
      description: 'Skepa-Lang is a modern programming language designed for developers who value simplicity and performance. It features a clean syntax inspired by popular languages while introducing innovative concepts to streamline development processes.',
      longDescription: 'Built from the ground up with developer experience in mind, Skepa-Lang combines the elegance of modern languages with the power needed for complex applications. Our compiler is optimized for speed, and the language includes built-in support for concurrency, making it perfect for both small scripts and large-scale applications.',
      features: [
        'Simple and intuitive syntax that reduces boilerplate',
        'High-performance JIT compilation for fast execution',
        'Cross-platform compatibility across Windows, macOS, and Linux',
        'Extensive standard library with modern utilities',
        'Built-in concurrency support with async/await',
        'Strong type system with inference capabilities',
      ],
      technologies: ['Rust', 'LLVM', 'Custom Parser', 'JIT Compiler'],
      status: 'Active Development',
      github: 'https://github.com/AayushMainali-Github/skepa-lang',
      docs: '#',
    },
    'skepa-db': {
      name: 'Skepa-DB',
      coverImage: '/assets/skepadb.png',
      description: 'Skepa-DB is a powerful database management system built for scalability and reliability. It supports advanced querying, real-time analytics, and seamless integration with modern applications.',
      longDescription: 'Designed for the demands of modern web applications, Skepa-DB offers unparalleled performance and flexibility. Whether you\'re building a small startup app or a large-scale enterprise system, Skepa-DB scales with your needs while maintaining data integrity and providing real-time insights.',
      features: [
        'Horizontally scalable architecture for massive datasets',
        'Real-time data processing and analytics capabilities',
        'Advanced security features including encryption at rest',
        'RESTful API for seamless integration',
        'ACID compliance for data consistency',
        'Advanced querying with SQL-like syntax',
        'Built-in replication and backup systems',
      ],
      technologies: ['Go', 'Distributed Systems', 'Custom Storage Engine', 'REST API'],
      status: 'Beta Release',
      github: 'https://github.com/AayushMainali-Github/skepa-db',
      docs: '#',
    },
  };

  const project = projects[name];

  if (!project) {
    return (
      <main className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl font-bold text-dark mb-4">Project Not Found</h1>
          <p className="text-secondary mb-8">The project you're looking for doesn't exist.</p>
          <Link to="/projects" className="text-primary hover:text-secondary font-semibold">
            ← Back to Projects
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/projects" className="text-primary hover:text-secondary font-semibold mb-8 inline-block">
          ← Back to Projects
        </Link>

        <div className="mb-8">
          <img src={project.coverImage} alt={project.name} className="w-full aspect-square object-cover rounded-lg mb-8" />
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
            <h1 className="text-4xl md:text-5xl font-bold text-dark mb-4 md:mb-0">{project.name}</h1>
            <span className={`px-4 py-2 rounded-full text-sm font-medium self-start ${
              project.status === 'Active Development' ? 'bg-yellow-100 text-yellow-800' :
              project.status === 'Beta Release' ? 'bg-blue-100 text-blue-800' :
              'bg-green-100 text-green-800'
            }`}>
              {project.status}
            </span>
          </div>
          <p className="text-xl text-secondary mb-6">{project.description}</p>
          <p className="text-lg text-dark leading-relaxed">{project.longDescription}</p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 mb-8">
          <div>
            <h2 className="text-2xl font-bold text-dark mb-4">Key Features</h2>
            <ul className="space-y-3">
              {project.features.map((feature, index) => (
                <li key={index} className="flex items-start">
                  <svg className="w-5 h-5 text-primary mr-3 mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-secondary">{feature}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h2 className="text-2xl font-bold text-dark mb-4">Technologies Used</h2>
            <div className="flex flex-wrap gap-2">
              {project.technologies.map((tech, index) => (
                <span key={index} className="bg-primary text-white px-3 py-1 rounded-full text-sm font-medium">
                  {tech}
                </span>
              ))}
            </div>
          </div>
        </div>

        <div className="bg-gray-50 rounded-lg p-6">
          <h2 className="text-2xl font-bold text-dark mb-4">Get Involved</h2>
          <p className="text-secondary mb-6">
            Interested in contributing to {project.name}? Check out the repository and join our community of developers.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <a
              href={project.github}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-primary text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition duration-300 text-center"
            >
              View on GitHub
            </a>
            <a
              href={project.docs}
              className="border-2 border-primary text-primary px-6 py-3 rounded-lg font-semibold hover:bg-primary hover:text-white transition duration-300 text-center"
            >
              Documentation
            </a>
          </div>
        </div>
      </div>
    </main>
  );
};

export default ProjectDetail;